import { createSlice } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";

/** Reference data shared across screens: zones, car types, countries. */

export const loadZones = apiThunk<any, void>("masters/zones", {
  server: "taxi",
  url: "zones/list/all",
  method: "GET",
});

/**
 * Car types are zone-scoped. The route with no zone segment exists but returns
 * every zone's cars, so pass the zone whenever it is known.
 */
export const loadCarTypes = apiThunk<any, { zoneId?: number | string } | void>(
  "masters/carTypes",
  {
    server: "taxi",
    url: (arg) =>
      arg && arg.zoneId ? `cartypes/active/cars/${arg.zoneId}` : "cartypes/active/cars",
    method: "GET",
  }
);

/**
 * The public country-code list. Note this is `customers/countrycodes/list`, not
 * `/countries` — the latter is the admin CRUD route and requires a JWT with the
 * countryManagement permission, so calling it from the login screen (where no
 * token exists yet) returns 401.
 */
export const loadCountries = apiThunk<any, void>("masters/countries", {
  server: "login",
  url: "customers/countrycodes/list",
  method: "GET",
});

/**
 * The country endpoint returns `{ mobile_code, country_name }` rows. Every
 * consumer wants an option pair, so the mapping happens once here rather than
 * in each screen.
 */
export interface CountryOption {
  label: string;
  value: string;
}

interface MastersState {
  zones: any[];
  carTypes: any[];
  countries: CountryOption[];
  loading: boolean;
}

const initialState: MastersState = {
  zones: [],
  carTypes: [],
  countries: [],
  loading: false,
};

const mastersSlice = createSlice({
  name: "masters",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadZones.fulfilled, (state, action) => {
        state.zones = action.payload?.data ?? [];
      })
      .addCase(loadCarTypes.fulfilled, (state, action) => {
        state.carTypes = action.payload?.data ?? [];
      })
      .addCase(loadCountries.fulfilled, (state, action) => {
        // A country with no dialling code cannot be selected and would collide
        // with every other blank one as an Autocomplete key, so drop it.
        state.countries = (action.payload?.data ?? [])
          .filter((item: any) => item?.mobile_code)
          .map((item: any) => ({
            label: item.country_name,
            value: String(item.mobile_code),
          }));
      });
  },
});

export default mastersSlice.reducer;
